import { useCallback, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';
import { Info } from 'lucide-react';

import { GetSiteLevels } from '@/api/fe-api/view/queries/get-site-levels';
import { SearchSelect } from '@/components/form/inputs/search-select';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { UpdateTask } from '@/task-manager/api/mutations/update-task';
import { ListFrontendIds } from '@/task-manager/api/queries/list-frontend-ids';
import type { TaskDetails } from '@/task-manager/entities/task-details';

import { TaskAttribute } from '../task-attribute';

export type TaskUpdateFrontendProps = {
  task: TaskDetails;
};

export function TaskUpdateFrontend({ task }: TaskUpdateFrontendProps) {
  const [inputLevel, setInputLevel] = useState<string | undefined>(undefined);
  const [inputId, setInputId] = useState<string | undefined>(undefined);
  const { assetId } = useCurrentAsset();

  const updateTask = UpdateTask.useMutation({
    onSuccess() {
      setInputLevel(undefined);
      setInputId(undefined);
    },
  });

  const frontendLevels = GetSiteLevels.useQuery({ siteId: assetId });
  const level = inputLevel ?? task.device?.frontendLevel;

  const frontendIds = ListFrontendIds.useQuery(level ? { level } : undefined);
  const id = inputId ?? task.device?.fullFrontendId;
  const idsLoading = frontendIds.isInitialLoading || frontendIds.isRefetching;
  const isWorkstreamTask = Boolean(task.projectManagement?.workstream.id);

  const handleSelectLevel = useCallback(
    function (frontendLevel?: string) {
      if (frontendLevel === level) {
        return;
      }
      setInputLevel(frontendLevel);
      setInputId('');
    },
    [level],
  );

  const handleSelectId = useCallback(
    function (frontendId?: string) {
      if (!level || !frontendId) {
        return;
      }

      updateTask.mutate({
        id: task.id,
        device: { frontendLevel: level, fullFrontendId: frontendId },
      });
    },
    [updateTask, task, level],
  );

  return (
    <TaskAttribute label="Device">
      <div className="space-y-4">
        <Select
          onValueChange={handleSelectLevel}
          value={level}
          disabled={idsLoading || isWorkstreamTask}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a level" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {(frontendLevels.data?.isOk()
                ? frontendLevels.data.value
                : []
              ).map((v) => (
                <SelectItem key={v} value={v.toString()}>
                  {v.toString()}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        <SearchSelect<string | undefined>
          value={id ?? ''}
          options={frontendIds.data?.ids ?? []}
          placeholder="Select an ID"
          optionKey={(n) => n ?? ''}
          renderValue={(n) => n ?? ''}
          renderOption={(n) => n ?? ''}
          searchBy={(id, query) => Boolean(id?.includes(query))}
          onSelect={handleSelectId}
          disabled={isWorkstreamTask}
          loading={updateTask.isLoading}
        />
      </div>
      {isWorkstreamTask && (
        <div className="mt-2 flex items-start gap-2 text-xs text-gray-500">
          <Info className="size-4" />
          The device cannot be changed for workstream tasks.
        </div>
      )}
    </TaskAttribute>
  );
}
